import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import { LayoutContext } from '../../contexts/LayoutContext'
import styles from './Layout.module.css'
import '../../index.css'
import { ArrowLeft24Filled, NavigationFilled } from '@fluentui/react-icons'
import NavMenu from '../../components/NavMenu/NavMenu'
import { DarkModeSync } from '../../components/DarkModeSync'
import { getCommunityLogo } from '../../api/api'
import { useConfig } from '../../contexts/ConfigContext'
import fallbackLogo from '../../assets/logo.webp'
import AccountSettingsModal from '../../components/AccountSettingsModal/AccountSettingsModal'

const Layout = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [selectedAnswer, setSelectedAnswer] = useState<number>(0)
    const [logoURL, setLogoURL] = useState<string | null>(null)
    const [loading, setIsLoading] = useState(true)
    const [settingsModalOpen, setSettingsModalOpen] = useState(false)
    const [pageTitle, setPageTitle] = useState<string | null>(null)
    const [onBack, setOnBack] = useState<(() => void) | null>(null)
    const [hideNav, setHideNav] = useState(false)
    const logoObjectUrlRef = useRef<string | null>(null)

    const {
        communityhubname,
        logoUrl: configuredLogoUrl,
        isLoading: isLoadingName,
    } = useConfig()

    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth <= 1024)
        checkIfMobile()
        window.addEventListener('resize', checkIfMobile)
        return () => window.removeEventListener('resize', checkIfMobile)
    }, [])

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                setIsLoading(true)

                if (logoObjectUrlRef.current) {
                    URL.revokeObjectURL(logoObjectUrlRef.current)
                    logoObjectUrlRef.current = null
                }

                if (configuredLogoUrl) {
                    const res = await fetch(configuredLogoUrl)
                    if (!res.ok)
                        throw new Error(`Logo fetch failed: ${res.status}`)
                    const contentType = res.headers.get('content-type') ?? ''
                    if (!contentType.startsWith('image/'))
                        throw new Error('No logo configured')
                    setLogoURL(configuredLogoUrl)
                    return
                }

                const imageURL = await getCommunityLogo(undefined)
                logoObjectUrlRef.current = imageURL
                setLogoURL(imageURL)
            } catch (error) {
                console.error('Failed to fetch community logo:', error)
                setLogoURL(fallbackLogo)
            } finally {
                setIsLoading(false)
            }
        }
        fetchLogo()
        return () => {
            if (logoObjectUrlRef.current) {
                URL.revokeObjectURL(logoObjectUrlRef.current)
                logoObjectUrlRef.current = null
            }
        }
    }, [configuredLogoUrl])
    const contextValue = useMemo(
        () => ({
            selectedAnswer,
            setSelectedAnswer,
            isMobile,
            logoURL,
            loading,
            communityhubname,
            isLoadingName,
            pageTitle,
            setPageTitle,
            onBack,
            setOnBack,
            hideNav,
            setHideNav,
        }),
        [
            selectedAnswer,
            isMobile,
            logoURL,
            loading,
            communityhubname,
            isLoadingName,
            pageTitle,
            onBack,
            hideNav,
        ]
    )

    return (
        <div className={styles.layout}>
            <DarkModeSync />
            <AccountSettingsModal
                settingsModalOpen={settingsModalOpen}
                setSettingsModalOpen={setSettingsModalOpen}
            />

            <div className={styles.mobileHeader}>
                {onBack && (
                    <button
                        className={styles.backButton}
                        onClick={onBack}
                        aria-label="Back"
                    >
                        <ArrowLeft24Filled aria-hidden="true" />
                    </button>
                )}
                {isMobile && pageTitle && (
                    <h1 className={styles.pageTitle}>{pageTitle}</h1>
                )}
                {!hideNav && (
                    <button
                        className={styles.burgerButton}
                        onClick={() => setIsMenuOpen(true)}
                        aria-label="Open navigation menu"
                        aria-expanded={isMenuOpen}
                        aria-controls="nav-menu"
                    >
                        <NavigationFilled style={{ fontSize: 24 }} />
                    </button>
                )}
            </div>

            <NavMenu
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                communityhubname={communityhubname}
                isLoadingName={isLoadingName}
                setSettingsModalOpen={setSettingsModalOpen}
            />
            <LayoutContext.Provider value={contextValue}>
                <div className={styles.contentContainer}>
                    <Outlet />
                </div>
            </LayoutContext.Provider>
        </div>
    )
}

export default Layout
